import type { ReactNode } from "react";
import {
  Image,
  type ImageSourcePropType,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { ArrowRight, CheckCircle2, MessageCircle } from "lucide-react-native";
import { CardSurface } from "@louise/moon-ui";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { semanticColors } from "@louise/theme/tokens";

type SecondaryAction = {
  label: string;
  onPress: () => void;
};

type Props = {
  title: string;
  body: string;
  ctaLabel: string;
  checklist: string[];
  backgroundImage: ImageSourcePropType;
  illustration?: ReactNode;
  onPress: () => void;
  secondaryAction?: SecondaryAction;
};

export function JourneyHandoffCard({
  title,
  body,
  ctaLabel,
  checklist,
  backgroundImage,
  illustration,
  onPress,
  secondaryAction,
}: Props) {
  const capturePress = usePressCapture("JourneyHandoffCard");
  return (
    <View style={{ marginTop: 46, overflow: "visible" }}>
      {illustration ? (
        <Pressable
          onPress={() => {
            capturePress(pressLabel(ctaLabel));
            onPress();
          }}
          accessibilityRole="button"
          accessibilityLabel={`${title}. ${ctaLabel}`}
          style={{
            position: "absolute",
            top: -56,
            alignSelf: "center",
            width: 174,
            height: 118,
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3,
          }}
        >
          {illustration}
        </Pressable>
      ) : null}

      {/* The feature shape, with the arch the illustration rests in kept on
          top. */}
      <CardSurface
        variant="feature"
        style={{
          borderTopLeftRadius: 132,
          borderTopRightRadius: 132,
          borderWidth: 1,
          borderColor: semanticColors.onDark.fill.strong,
        }}
      >
        <Image
          source={backgroundImage}
          style={[StyleSheet.absoluteFill, { width: "100%", height: "100%" }]}
          resizeMode="cover"
        />

        <View
          style={{
            paddingHorizontal: 22,
            paddingTop: illustration ? 58 : 28,
            paddingBottom: 20,
            backgroundColor: semanticColors.onDark.fill.subtle,
            alignItems: "center",
            ...(Platform.OS === "web"
              ? ({
                  backdropFilter: "blur(40px)",
                  WebkitBackdropFilter: "blur(40px)",
                } as Record<string, string>)
              : {}),
          }}
        >
          {/* Reading is not pressing. The card used to be one big Pressable, so
              every attempt to read the title, the body or the checklist — or to
              select a word of it — fired the action instead. Only the button
              below is a button. */}
          <View style={{ width: "100%", alignItems: "center" }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 23,
                lineHeight: 28,
                color: semanticColors.onDark.text.primary,
                fontWeight: "700",
                letterSpacing: -0.2,
                textAlign: "center",
              }}
            >
              {title}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                lineHeight: 19,
                color: semanticColors.onDark.text.secondary,
                fontWeight: "500",
                marginTop: 6,
                textAlign: "center",
                width: "100%",
                maxWidth: 390,
              }}
            >
              {body}
            </Text>

            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                marginTop: 18,
                width: "100%",
                maxWidth: 390,
              }}
            >
              {checklist.map((label) => (
                <View
                  key={label}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 5,
                    maxWidth: "100%",
                  }}
                >
                  <CheckCircle2
                    size={12}
                    color={semanticColors.onDark.text.primary}
                    strokeWidth={1.8}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      lineHeight: 16,
                      color: semanticColors.onDark.text.primary,
                      fontWeight: "700",
                      flexShrink: 1,
                    }}
                  >
                    {label}
                  </Text>
                </View>
              ))}
            </View>

            <Pressable
              onPress={() => {
                capturePress(pressLabel(ctaLabel));
                onPress();
              }}
              accessibilityRole="button"
              accessibilityLabel={`${title}. ${ctaLabel}`}
              style={({ pressed }) => ({
                marginTop: 22,
                alignSelf: "stretch",
                transform: [{ scale: pressed ? 0.995 : 1 }],
              })}
            >
              <LinearGradient
                colors={[
                  "rgba(255, 255, 255, 0.13)",
                  "rgba(255, 255, 255, 0.06)",
                ]}
                start={{ x: 0, y: 0 }}
                end={{ x: 0, y: 1 }}
                style={{
                  paddingVertical: 14,
                  paddingHorizontal: 20,
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  borderWidth: 1,
                  borderColor: semanticColors.onDark.fill.subtle,
                  borderRadius: 14,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 15,
                    fontWeight: "600",
                    color: semanticColors.onDark.text.primary,
                    letterSpacing: 0.2,
                  }}
                >
                  {ctaLabel}
                </Text>
                <ArrowRight
                  size={16}
                  color={semanticColors.onDark.text.primary}
                  strokeWidth={2}
                />
              </LinearGradient>
            </Pressable>
          </View>

          {secondaryAction ? (
            <Pressable
              onPress={() => {
                capturePress(pressLabel(secondaryAction.label));
                secondaryAction.onPress();
              }}
              accessibilityRole="button"
              accessibilityLabel={secondaryAction.label}
              style={({ pressed }) => ({
                marginTop: 10,
                alignSelf: "stretch",
                paddingVertical: 13,
                paddingHorizontal: 20,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 9,
                borderWidth: 1,
                borderColor: "rgba(255, 255, 255, 0.34)",
                borderRadius: 14,
                backgroundColor: pressed
                  ? semanticColors.onDark.fill.strong
                  : semanticColors.onDark.fill.subtle,
                ...(Platform.OS === "web"
                  ? ({ cursor: "pointer" } as Record<string, string>)
                  : {}),
              })}
            >
              <MessageCircle
                size={16}
                color={semanticColors.onDark.text.primary}
                strokeWidth={1.9}
              />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 15,
                  fontWeight: "600",
                  color: semanticColors.onDark.text.primary,
                  letterSpacing: 0.2,
                }}
              >
                {secondaryAction.label}
              </Text>
            </Pressable>
          ) : null}
        </View>
      </CardSurface>
    </View>
  );
}
